import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { FormApi } from 'final-form';
import { useFormState } from 'react-final-form';

import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { ISmevMessageResponse } from '../../../APIPackage/SmevMessagesModule/interfaces/ISmevMessageResponse';
import styles from './SmevRequest.module.scss';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { SMEV_REQUEST_ID, SMEV_RESPONSE_ID } from '../../../KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IRequestType } from '../../../APIPackage/SmevMessagesModule/interfaces/IRequestType';
import { ISmevRequestForm } from '../interfaces/ISmevRequestForm';
import { createSelectOption } from '../functions/commonFunctions';
import { IRequestTemplate } from '../../../APIPackage/SmevMessagesModule/interfaces/IRequestTemplate';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { RequestTypeEnum } from '../../../APIPackage/SmevMessagesModule/enums/RequestTypeEnum';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_MINUTE_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { ISmevRequestTemplateOption } from '../interfaces/ISmevRequestTemplateOption';
import { createSmevMessageTypeOption, ISmevMessageTypeOption } from '../interfaces/ISmevMessageTypeOption';
import { XmlFieldActions } from './XmlFieldActions';
import { XmlEditorField } from '../../../UIPackage/MainModule/components/XmlEditorField';
import { formatXml, validateXml } from '../../../CorePackage/MainModule/common/xmlHelpers';
import { SmevAttachments } from './SmevAttachments';
import { SmevMessageProcessingStatusEnum } from '../../../APIPackage/SmevMessagesModule/enums/SmevMessageProcessingStatusEnum';

interface ISmevMessageProps {
  isRequestCreations: boolean;
  form: FormApi<ISmevRequestForm, Partial<ISmevRequestForm>>;
  requestTemplates?: IRequestTemplate[];
  requestTypes?: IRequestType[];
  setXmlError: Dispatch<SetStateAction<string | undefined>>;
  smevRequestFindResponse?: ISmevMessageResponse;
  xmlError?: string;
  onRefreshRequest: () => void;
  onSendRequest: () => void;
}

const mapTemplateToOption = (template: IRequestTemplate): ISmevRequestTemplateOption => ({
  ...template,
  name: template.name ?? '',
  label: template.name ?? '',
  value: template.id,
});

export const SmevRequestBody = ({
  isRequestCreations,
  form,
  requestTemplates,
  requestTypes,
  smevRequestFindResponse,
  xmlError,
  setXmlError,
  onRefreshRequest,
  onSendRequest,
}: ISmevMessageProps) => {
  const { values } = useFormState<ISmevRequestForm>({ subscription: { values: true } });
  const {
    messageType,
    typeOfRequest,
    adapterType,
    requestUri,
    requestXml,
    attachments,
    requestStatus,
    responseStatus,
  } = values;
  const hasUploadingAttachments = Boolean(attachments?.some(attachment => attachment.isNew && !attachment.guid));
  const requestMessage = smevRequestFindResponse?.requestMessage;
  const responseMessage = smevRequestFindResponse?.responseMessage;

  const isIncomingRequest = typeOfRequest === RequestTypeEnum.RECEIVE;
  const isOutgoingRequest = typeOfRequest === RequestTypeEnum.SEND;

  const requestProcessingStatus = requestMessage?.status || requestStatus;
  const responseProcessingStatus = responseMessage?.status || responseStatus;

  const isNonFinalStatus = (status?: string) =>
    !status || status === SmevMessageProcessingStatusEnum.NEW || status === SmevMessageProcessingStatusEnum.IN_PROGRESS;

  // Для исходящего запроса опрашиваем статус ответа, для входящего — статус самого запроса.
  const isRefreshVisible = isOutgoingRequest
    ? isNonFinalStatus(responseProcessingStatus)
    : isNonFinalStatus(requestProcessingStatus);

  const adapterTypeSelectOptions = useMemo(() => {
    const adapters = new Set(
      requestTypes?.map(item => item.mnemonic).filter((mnemonic): mnemonic is string => !!mnemonic) ?? [],
    );

    return Array.from(adapters).map(item => createSelectOption(item));
  }, [requestTypes]);

  const messageTypeSelectOptions = useMemo(() => {
    const typesWithMessageType = requestTypes?.filter(item => !!item.messageType) ?? [];

    if (!adapterType?.value) {
      return typesWithMessageType.map(item => createSmevMessageTypeOption(item));
    }

    return typesWithMessageType
      .filter(item => item.mnemonic === adapterType.value)
      .map(item => createSmevMessageTypeOption(item));
  }, [adapterType?.value, requestTypes]);

  const requestTemplateOptions = useMemo(() => {
    const templatesWithName = requestTemplates?.filter(template => !!template.name) ?? [];

    if (!messageType || !requestUri) {
      return [];
    }

    return templatesWithName.filter(template => template.uri === requestUri).map(mapTemplateToOption);
  }, [messageType, requestTemplates, requestUri]);

  useEffect(() => {
    if (requestMessage) {
      setXmlError(undefined);
      const requestMessageXml = requestMessage.message;
      const { formatted: formattedRequestXml } = formatXml(requestMessageXml);

      const matchedMessageType = requestTypes?.find(item => item.uri === smevRequestFindResponse.uri);

      form.change('adapterType', createSelectOption(smevRequestFindResponse.adapterType));
      form.change('messageType', matchedMessageType ? createSmevMessageTypeOption(matchedMessageType) : undefined);
      form.change('requestUri', smevRequestFindResponse.uri ?? matchedMessageType?.uri);
      form.change('requestXml', formattedRequestXml ?? requestMessageXml);
      form.change(
        'requestTime',
        formatDate({
          date: smevRequestFindResponse.requestMessage.dateTime || '',
          dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
          parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
        }),
      );
      form.change('requestStatus', smevRequestFindResponse.requestMessage.status);
      form.change('typeOfRequest', smevRequestFindResponse.requestType);
    }

    if (responseMessage) {
      const responseMessageContent = responseMessage.message;
      const { formatted: formattedResponseXml } = formatXml(responseMessageContent);

      form.change('responseXml', formattedResponseXml ?? responseMessageContent);
      form.change(
        'responseTime',
        formatDate({
          date: responseMessage.dateTime || '',
          dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
          parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
        }),
      );
      form.change('responseStatus', responseMessage.status);
    }
  }, [requestTypes, setXmlError, responseMessage, requestMessage]);

  useEffect(() => {
    setXmlError(undefined);
  }, [requestXml, setXmlError]);

  const handleAdapterTypeChange = useCallback(() => {
    form.change('messageType');
    form.change('requestUri');
    form.change('typeOfRequest');
    form.change('requestTemplate');
    form.change('requestXml');
    form.change('attachments');
    setXmlError(undefined);
  }, [form, setXmlError]);

  const handleMessageTypeChange = useCallback(
    (value: ISmevMessageTypeOption | undefined) => {
      if (value?.mnemonic && adapterType?.value !== value.mnemonic) {
        form.change('adapterType', createSelectOption(value.mnemonic));
      }

      form.change('requestUri', value?.uri);
      form.change('typeOfRequest', value?.requestType as RequestTypeEnum | undefined);
      form.change('attachments');
      setXmlError(undefined);

      const messageTypeUri = value?.uri;
      const templatesForMessageType = messageTypeUri
        ? requestTemplates?.filter(template => !!template.name && template.uri === messageTypeUri) ?? []
        : [];

      if (templatesForMessageType.length === 1) {
        const singleTemplateOption = mapTemplateToOption(templatesForMessageType[0]);

        form.change('requestTemplate', singleTemplateOption);
        form.change('requestXml', singleTemplateOption.xmlTemplate);
      } else {
        form.change('requestTemplate');
        form.change('requestXml');
      }
    },
    [adapterType?.value, form, requestTemplates, setXmlError],
  );

  const handleRequestTemplateChange = useCallback(
    (value: ISmevRequestTemplateOption | undefined) => {
      form.change('requestXml', value?.xmlTemplate);
      setXmlError(undefined);

      if (!value) {
        return;
      }

      if (!requestUri) {
        form.change('requestUri', value.uri);
      }
    },
    [form, requestUri, setXmlError],
  );

  const handleFormatXml = useCallback(() => {
    const { formatted, error } = formatXml(requestXml);

    if (error) {
      setXmlError(error);

      return;
    }

    setXmlError(undefined);
    form.change('requestXml', formatted);
  }, [form, requestXml, setXmlError]);

  const handleValidateXml = useCallback(() => {
    const { isValid, error } = validateXml(requestXml);

    setXmlError(isValid ? undefined : error);

    return isValid;
  }, [requestXml, setXmlError]);

  const handleSendRequest = useCallback(() => {
    if (!handleValidateXml()) {
      return;
    }

    onSendRequest();
  }, [handleValidateXml, onSendRequest]);

  const handleUploadXml = useCallback(
    (content: string) => {
      form.change('requestXml', content);
      setXmlError(undefined);
    },
    [form, setXmlError],
  );

  const handleUploadXmlError = useCallback(
    (error: string) => {
      setXmlError(error);
    },
    [setXmlError],
  );

  const mainLabel = useMemo(() => {
    if (isRequestCreations) {
      return 'Новый запрос';
    }

    if (typeOfRequest === RequestTypeEnum.RECEIVE) {
      return 'Входящий запрос';
    }

    if (typeOfRequest === RequestTypeEnum.SEND) {
      return 'Исходящий запрос';
    }

    return 'Запрос';
  }, [isRequestCreations, typeOfRequest]);

  return (
    <>
      <KnmCollapse theme="fields" title={mainLabel} id={SMEV_REQUEST_ID} opened={true}>
        <>
          <Row>
            <FieldBlock label="Адаптер" placementLabel="left" labelSize="col-5">
              <SelectInputField
                className="shared-row-field"
                name={`adapterType`}
                options={adapterTypeSelectOptions}
                disabled={!isRequestCreations}
                size="medium"
                onChange={handleAdapterTypeChange}
              />
            </FieldBlock>
          </Row>

          <Row>
            <FieldBlock label="Вид сведений" placementLabel="left" labelSize="col-5">
              <SelectInputField
                className="shared-row-field"
                name="messageType"
                options={messageTypeSelectOptions}
                disabled={!isRequestCreations}
                size="medium"
                onChange={handleMessageTypeChange}
                isSearchable
              />
            </FieldBlock>
          </Row>

          {messageType && isRequestCreations && (
            <Row>
              <FieldBlock label="URI" placementLabel="left" labelSize="col-5">
                <TextInputField className="shared-row-field" name="requestUri" disabled={true} />
              </FieldBlock>
            </Row>
          )}

          {isRequestCreations && (
            <Row>
              <FieldBlock label="Шаблон запроса" placementLabel="left" labelSize="col-5">
                <SelectInputField
                  className="shared-row-field"
                  name="requestTemplate"
                  options={requestTemplateOptions}
                  disabled={!isRequestCreations}
                  size="medium"
                  onChange={handleRequestTemplateChange}
                />
              </FieldBlock>
            </Row>
          )}

          <Row>
            <FieldBlock
              label="XML запрос"
              error={xmlError}
              className={styles.XmlRequestField}
              button={
                isRequestCreations ? (
                  <XmlFieldActions
                    disabled={!adapterType || !messageType}
                    onFormat={handleFormatXml}
                    onValidate={handleValidateXml}
                    onUpload={handleUploadXml}
                    onUploadError={handleUploadXmlError}
                  />
                ) : undefined
              }
            >
              <XmlEditorField
                name="requestXml"
                disabled={!isRequestCreations || !adapterType || !messageType}
                className={styles.XmlRequestTextarea}
                placeholder="Вставьте XML запрос, который должен быть в <MessagePrimaryContent>"
              />
            </FieldBlock>
          </Row>

          {isRequestCreations && (
            <Row>
              <SmevAttachments form={form} disabled={!adapterType || !messageType} />
            </Row>
          )}

          {!isRequestCreations && (
            <Row>
              <FieldBlock
                label="Время запроса"
                placementLabel="left"
                size="col-3"
                labelSize="col-2"
                className={styles.BasicLeftWidthField}
              >
                <TextInputField name="requestTime" disabled={true} />
              </FieldBlock>

              {isIncomingRequest && (
                <FieldBlock
                  label="Статус обработки"
                  placementLabel="left"
                  size="col-3"
                  labelSize="col-2"
                  className={styles.BasicLeftWidthField}
                >
                  <TextInputField className="shared-row-field" name="requestStatus" disabled={true} />
                </FieldBlock>
              )}
            </Row>
          )}

          {isRequestCreations && (
            <Button
              className={styles.CreationButton}
              variant="contained"
              id="createRequest"
              color="primary"
              type="button"
              text="Отправить"
              disabled={!requestXml || !requestUri || !typeOfRequest || !!xmlError || hasUploadingAttachments}
              onClick={handleSendRequest}
            />
          )}
        </>
      </KnmCollapse>

      {requestMessage && !isRequestCreations && (
        <KnmCollapse theme="fields" title={'Ответ'} id={SMEV_RESPONSE_ID} opened={true}>
          <>
            <Row notWrap>
              <FieldBlock
                label="Время ответа"
                placementLabel="left"
                size="col-3"
                labelSize="col-2"
                className={styles.BasicLeftWidthField}
              >
                <TextInputField name="responseTime" disabled={true} />
              </FieldBlock>

              {isOutgoingRequest && (
                <FieldBlock
                  label="Статус обработки"
                  placementLabel="left"
                  size="col-3"
                  labelSize="col-2"
                  className={styles.BasicLeftWidthField}
                >
                  <TextInputField className="shared-row-field" name="responseStatus" disabled={true} />
                </FieldBlock>
              )}

              {isRefreshVisible && (
                <Button
                  variant="contained"
                  id="refreshSmevResponse"
                  color="primary"
                  type="button"
                  text="Обновить"
                  onClick={onRefreshRequest}
                />
              )}
            </Row>
            <Row>
              <FieldBlock label="XML ответ">
                <XmlEditorField name="responseXml" disabled={true} className={styles.XmlRequestTextarea} />
              </FieldBlock>
            </Row>
          </>
        </KnmCollapse>
      )}
    </>
  );
};
